import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Check, X, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Pricing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const [pricing, setPricing] = useState(null);
  const [pricingError, setPricingError] = useState(false);
  const [busy, setBusy] = useState(null);
  const [selectedBundleIdx, setSelectedBundleIdx] = useState(0);
  const [expiredMessage, setExpiredMessage] = useState(null);

  useEffect(() => {
    if (params.get("expired_pass") === "1") {
      setExpiredMessage(
        "Your Bulk-Delete Pass has expired. Purchase a monthly plan to continue along with auto deletion rules and subscription management."
      );
    }
    api.get("/payments/pricing")
      .then((r) => {
        setPricing(r.data.pricing);
        setPricingError(false);
      })
      .catch(() => {
        setPricingError(true);
        setPricing({ bulk_delete: { bundles: [] }, plans: {} });
      });
  }, [location.search]);

  const bd = pricing?.bulk_delete;
  const plans = pricing?.plans;
  const bundles = bd?.bundles || [];
  const selected = bundles[selectedBundleIdx];

  const subscribe = async (plan_id) => {
    if (!user) return nav("/login");
    setBusy(plan_id);
    try {
      const { data } = await api.post("/payments/checkout/subscription", { plan_id, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error("Failed to start checkout");
      setBusy(null);
    }
  };

  const buyPass = async () => {
    if (!user) return nav("/login");
    if (!selected) return;
    setBusy("bulk_delete");
    try {
      const { data } = await api.post("/payments/checkout/pass", { inbox_count: selected.inboxes, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start checkout");
      setBusy(null);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/easy-mail-logo.png" alt="Easy Mail" className="h-7 w-auto" />
            <span className="font-display text-xl font-bold">Easy Mail</span>
          </Link>
          <div className="flex gap-3">
            <Link to="/login"><Button variant="ghost" className="rounded-none">Sign in</Button></Link>
            <Link to="/register"><Button className="rounded-none btn-brutalist">Get started</Button></Link>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-20">
        {expiredMessage && (
          <div className="mb-8 rounded-md border border-yellow-400 bg-yellow-50 p-6 text-yellow-900">
            <p className="font-semibold">Your Bulk-Delete Pass has expired.</p>
            <p>{expiredMessage}</p>
          </div>
        )}
        {pricingError && (
          <div className="mb-8 rounded-md border border-rose-400 bg-rose-50 p-6 text-rose-900">
            <p className="font-semibold">Pricing data failed to load.</p>
            <p>Please refresh the page or try again later. Your plan details may still be available after a retry.</p>
          </div>
        )}
        <div className="section-tag mb-4">/ Pricing</div>
        <h1 className="font-display text-5xl sm:text-6xl mb-4">Bulk Delete, Auto rules and Subscriptions, or Both.</h1>
        <p className="text-lg text-foreground max-w-2xl mb-12">
          Bulk Delete Pass expired? Purchase a monthly plan to continue. Bulk Delete Pass bundles give you a one-time 7-day cleanup window. Monthly subscriptions give you automated rules and ongoing inbox deletion. 
          
          SPECIAL ACCESS RULE: If you have ever purchased a Bulk Delete Pass bundle AND have an active monthly subscription, Bulk Delete + Analytics access are automatically restored for the lifetime of your subscription — no need to re-purchase a bundle.
        </p>

        {/* Bulk Delete Pass — bundles */}
        {bd && (
          <div className="border-2 border-foreground bg-background p-8 mb-12" data-testid="card-pass">
            <div className="grid lg:grid-cols-2 gap-8 items-start">
              <div>
                <div className="font-mono text-xs uppercase tracking-widest mb-3 text-primary">One-time · 7-day access</div>
                <h2 className="font-display text-4xl font-bold mb-3">Bulk Delete Pass</h2>
                <p className="text-foreground/80 mb-6">Full bulk + filtered cleanup tools, plus analytics. Pick a bundle that fits your inbox count.</p>

                <div className="space-y-2 mb-6">
                  {bd.features.map((f) => (
                    <div key={f} className="flex items-start gap-2 text-sm"><Check size={18} weight="bold" className="shrink-0 mt-0.5 text-primary" />{f}</div>
                  ))}
                  {bd.excludes.map((f) => (
                    <div key={f} className="flex items-start gap-2 text-sm text-muted-foreground"><X size={18} weight="bold" className="shrink-0 mt-0.5" />{f}</div>
                  ))}
                </div>
              </div>

              <div className="border border-border p-6 bg-secondary/40">
                <div className="section-tag mb-3">Choose a bundle</div>
                <div className="grid grid-cols-2 gap-2 mb-5" data-testid="bundle-grid">
                  {bundles.map((b, i) => {
                    const isSel = i === selectedBundleIdx;
                    return (
                      <button
                        key={b.inboxes}
                        onClick={() => setSelectedBundleIdx(i)}
                        data-testid={`bundle-${b.inboxes}`}
                        className={`border p-3 text-left transition-all ${isSel ? "border-foreground bg-foreground text-background" : "border-border bg-background hover:border-foreground/60"}`}
                      >
                        <div className="font-mono text-xs uppercase tracking-widest opacity-70">{b.inboxes} inbox{b.inboxes === 1 ? "" : "es"}</div>
                        <div className="font-mono text-xl font-bold">${b.price.toFixed(2)}</div>
                      </button>
                    );
                  })}
                </div>
                {selected && (
                  <>
                    <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1">Selected bundle</div>
                    <div className="font-mono text-5xl font-bold mb-4" data-testid="bundle-total">${selected.price.toFixed(2)}</div>
                    <div className="text-xs text-muted-foreground mb-4 font-mono">{selected.inboxes} inbox{selected.inboxes === 1 ? "" : "es"} · 7-day access window</div>
                  </>
                )}
                <Button onClick={buyPass} disabled={busy === "bulk_delete" || !selected} className="w-full rounded-none btn-brutalist h-12 gap-2" data-testid="buy-pass-btn">
                  {busy === "bulk_delete" ? "Redirecting…" : "Buy Bulk Delete Pass"} <ArrowRight size={16} />
                </Button>
                <p className="text-xs text-muted-foreground mt-3 font-mono italic">{bd.policy || ""}</p>
              </div>
            </div>
          </div>
        )}

        <div className="section-tag mb-4">/ Monthly subscriptions</div>
        <p className="text-base text-muted-foreground mb-8">Automated cleanup. Auto-rules. Subscription management. Cancel any time.</p>

        <div className="grid md:grid-cols-3 gap-px bg-border">
          {plans && Object.entries(plans).map(([id, p], idx) => {
            const featured = id === "family";
            return (
              <div key={id} className={`p-8 ${featured ? "bg-foreground text-background" : "bg-background"}`} data-testid={`plan-${id}`}>
                {featured && <div className="font-mono text-xs uppercase tracking-widest mb-3 text-primary">Most popular</div>}
                <div className="font-mono text-xs uppercase tracking-widest mb-2 opacity-70">{`0${idx + 1}`}</div>
                <h3 className="font-display text-3xl font-bold mb-2">{p.name}</h3>
                <div className="mb-6">
                  <span className="font-mono text-5xl font-bold">${p.price_monthly.toFixed(2)}</span>
                  <span className="text-sm opacity-70 ml-1">/ month</span>
                </div>
                <ul className="space-y-3 mb-8 text-sm">
                  <li className="flex items-start gap-2"><Check size={18} weight="bold" className="shrink-0 mt-0.5" />{p.included_inboxes} inboxes included</li>
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2"><Check size={18} weight="bold" className="shrink-0 mt-0.5" />{f}</li>
                  ))}
                  {p.excludes.map((f) => (
                    <li key={f} className={`flex items-start gap-2 ${featured ? "text-background/60" : "text-muted-foreground"}`}><X size={18} weight="bold" className="shrink-0 mt-0.5" />{f}</li>
                  ))}
                </ul>
                <Button
                  onClick={() => subscribe(id)}
                  disabled={busy === id}
                  className={`w-full rounded-none h-12 gap-2 btn-brutalist ${featured ? "bg-primary text-primary-foreground hover:bg-primary" : ""}`}
                  data-testid={`subscribe-${id}`}
                >
                  {busy === id ? "Redirecting…" : "Subscribe"} <ArrowRight size={16} />
                </Button>
              </div>
            );
          })}
        </div>

        <div className="border border-border p-6 mt-12 bg-secondary/40 text-sm">
          <div className="font-mono text-xs uppercase tracking-widest mb-2">Special access rule</div>
          <p>If you have ever purchased a Bulk Delete Pass bundle AND have an active monthly subscription, Bulk Delete + Analytics access are automatically restored for the lifetime of your subscription — no need to re-purchase a bundle.</p>
        </div>

      </div>

      <div data-testid="footer-legal" className="border-t border-border py-6 text-center text-xs text-muted-foreground">
        <a href="/legal" className="underline mx-2">Legal Documents and Policies</a>
      </div>
    </div>
  );
}
